'use client';

import { PortableText } from '@portabletext/react';
import Image from 'next/image';

import { GroqImage } from '@/helpers/Image';
import type { PagePatrickAffiliateSchema } from '@/sanity/pages/PagePatrickAffiliateTemplate';
import { measuredRichText } from './richtext';
import ChevronCta from './ChevronCta';

import signature from '@/public/patrick-affiliate/signature.svg';

/**
 * "About <partner>" — Figma 8841:2361 (desktop) / 8811:4423 (mobile).
 *
 * The two breakpoints order the content differently: on desktop the portrait
 * sits in a second column beside the whole text block, on mobile it slots
 * between the heading and the body copy. A two-row grid gives both from one
 * DOM order — the image just spans both rows of the second column. Mobile
 * also puts the signature *above* the button, so that pair flips too.
 */
export default function AboutSection({
  data,
}: {
  data: PagePatrickAffiliateSchema;
}) {
  if (
    !data?.aboutChip &&
    !data?.aboutHeading?.length &&
    !data?.aboutText?.length &&
    !data?.aboutCTA &&
    !data?.aboutImage
  )
    return null;

  return (
    <section className="bg-white px-[2rem] py-[8rem] lg:px-0 lg:py-[15rem]">
      <div className="grid mx-auto w-[min(160rem,100%)] grid-cols-1 gap-y-[3rem] lg:grid-cols-[67.125rem_79.25rem] lg:justify-between lg:items-center lg:gap-y-[1.75rem]">
        {(data?.aboutChip || data?.aboutHeading?.length > 0) && (
          <div className="flex flex-col items-center gap-[2rem] lg:col-start-1 lg:row-start-1 lg:items-start">
            {data?.aboutChip && (
              <div className="flex items-center bg-black/[4%] border border-black/[4%] gap-[1.25rem] px-[2.5rem] py-[1.5rem]">
                <span className="rounded-full shrink-0 bg-[#0D2922] w-[0.75rem] h-[0.75rem]" />
                <p className="m-0 uppercase font-geist font-[500] leading-[1.146] text-black/[64%] text-[1.75rem]">
                  {data.aboutChip}
                </p>
              </div>
            )}
            {data?.aboutHeading?.length > 0 && (
              <div className="richtext richtext-center lg:richtext-start prose-headings:text-black prose-headings:font-geist prose-headings:font-[500] prose-headings:leading-none prose-headings:text-[4.5rem] prose-headings:tracking-[-0.225rem] lg:prose-headings:text-[8rem] lg:prose-headings:tracking-[-0.4rem] [--hl-dy:0.65rem] lg:[--hl-dy:0.125rem]">
                <PortableText
                  value={data.aboutHeading}
                  components={measuredRichText}
                />
              </div>
            )}
          </div>
        )}

        {data?.aboutImage && (
          <div className="w-full border rounded-[1rem] border-black/[16%] p-[0.75rem] lg:col-start-2 lg:row-start-1 lg:row-span-2">
            <GroqImage
              src={data.aboutImage}
              alt={data?.aboutImage?.name ?? ''}
              quality={100}
              // Opts out of the helper's px/8 -> style.width behaviour, which
              // would otherwise size this off the source asset's pixel width.
              style={{ width: '100%' }}
              // The design mirrors the portrait so he faces the copy.
              className="object-cover -scale-x-100 !w-full rounded-[0.5rem] !h-[52.625rem] lg:!h-[90.375rem]"
            />
          </div>
        )}

        {(data?.aboutText?.length > 0 || data?.aboutCTA) && (
          <div className="flex flex-col gap-[3rem] lg:col-start-1 lg:row-start-2 lg:gap-[4.5rem]">
            {data?.aboutText?.length > 0 && (
              <div className="richtext richtext-start prose-p:font-geist prose-p:font-[400] prose-p:text-[2.25rem] prose-p:leading-[1.33] prose-p:text-black/[60%] lg:prose-p:text-black/[70%] [&_p+p]:mt-[3rem]">
                <PortableText
                  value={data.aboutText}
                  components={measuredRichText}
                />
              </div>
            )}
            {data?.aboutCTA && (
              <div className="flex flex-col-reverse items-start w-full gap-[3rem] lg:flex-col">
                <ChevronCta
                  data={data.aboutCTA}
                  language={data?.settings?.language}
                />
                <Image
                  src={signature}
                  alt=""
                  className="h-auto pointer-events-none w-[25.625rem] ml-[1.375rem] lg:ml-0"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
